import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from '../context/AuthContext';
import { useError } from '../context/ErrorContext';
import { useLoading } from '../context/LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const CompanyWorkForceContext = createContext();
 
export const useCompanyWorkForce = () => {
  const context = useContext(CompanyWorkForceContext);
 
  if (!context) {
    throw new Error('useCompanyWorkForce must be used within a CompanyWorkForceProvider');
  }
  return context;
};
 

export const CompanyWorkForceProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [workForce, setWorkForce] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/company-workforces');

  const getWorkForce = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('users?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('users');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setWorkForce(response.data.users || []);
       
        
        if(filters){
          return response.data.users;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch workforce information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getWorkForceByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`users/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let users = {'data':[response.data.user]}
      setWorkForce(users || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/company-workforces/edit/'+id))){
        if (getToken()) {
        
          const apiInstance = Api(() => getToken());
          setApi(apiInstance);
          await getWorkForceByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getWorkForceByIdApi, searchParams]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
        const apiInstance = Api(() => getToken());
        setApi(apiInstance);

        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getWorkForce(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getWorkForce, searchParams, initialized,hideLoading]);

  const getManagerSupervisor = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('users?role=manager_supervisor', 'GET', null, true);
    if(response.status === 200){
     
      return response.data.users;
    }else{
    
    }
  }, [getToken]);

  const getAccessPermissions = useCallback(async (type) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('permissions?type='+type, 'GET', null, true);
    
    if(response.status === 200){
     
      return response.data.permissions;
    }else{
     
    }
  }, [getToken]);

  const getEmploymentSettings = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-all-users-employment-settings', 'GET', null, true);
    if(response.status === 200){
      
      return response.data;
    }else{
      
    }
  
  }, [getToken]);

  const getPayrollSettings = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-all-users-payroll-settings', 'GET', null, true);
    if(response.status === 200){
      
      return response.data;
    }else{
      
    }
  }, [getToken]);

   const getDocumentSettings = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-all-users-document-settings', 'GET', null, true);
    if(response.status === 200){
    
      return response.data;
    }else{
     
    }
   }, [getToken]);

  const addWorkForce = async (data) => {
    const api = Api(() => getToken());
    
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(data).forEach(key => {
      const value = data[key];
    
      if (value !== null && value !== undefined) {
        // Special handling for company_ids (or any array field)
      
         if (Array.isArray(value)) {
          value.forEach((v,index) => {
          
              if(key === 'documents' && v.file){
                formData.append(`document_files[${index}]`, v.file);
                formData.append(`documents[]`, JSON.stringify(v));
              }else if (key === 'company_ids') {
               
                const cleaned = isNaN(v) ? v : Number(v);
                formData.append('company_ids[]', cleaned);
              
            }   else{
              formData.append(`${key}[]`, JSON.stringify(v));
            }
          });
        } 
        // Normal field
        else {
          if(key === 'schedules'){
            if(typeof value === 'string'){
              formData.append('schedules[]', value);
            } else{
              formData.append('schedules[]', JSON.stringify(value));
            }
          } else{ 
            formData.append(key, value);
          }
        }
      }
    });

    try {
      const response = await api.call('users', 'POST', formData, true, true);
     
      if (response.status === 200) {
        const users = response.data.users;
        setWorkForce(users);
        hideLoading();
        return { status: response.status };
      } else {
        hideLoading();
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add workforce member'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      
      }
    } catch (error) {
      hideLoading();
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add workforce member'
      });
      return {
        status: 'error',
        error: error
      };
    }
  };

  const getWorkForceById =  (id) => {
    
    let workForceData = workForce?.data?.find(worker => {
      return Number(worker.id) === Number(id);
    });
   
    return workForceData;
  };

  const updateWorkForce = async (id, updatedData) => {
    if (!api) {
      return;
    }
    
    const formData = new FormData();
    Object.keys(updatedData).forEach(key => {
      const value = updatedData[key];
    
      if (value !== null && value !== undefined) {
        // Special handling for company_ids (or any array field)
      
         if (Array.isArray(value)) {
          value.forEach((v,index) => {
          
              if(key === 'documents' && v.file){
                formData.append(`document_files[${index}]`, v.file);
                formData.append(`documents[]`, JSON.stringify(v));
              }else if (key === 'company_ids') {
               
                const cleaned = isNaN(v) ? v : Number(v);
                formData.append('company_ids[]', cleaned);
              
            }   else{
              formData.append(`${key}[]`, JSON.stringify(v));
            }
          });
        } 
        // Normal field
        else {
          if(key === 'schedules'){
            if(typeof value === 'string'){
              formData.append('schedules[]', value);
            } else{
              formData.append('schedules[]', JSON.stringify(value));
            }
          } else{ 
            formData.append(key, value);
          }
        }
      }
    });

    try {
      const response = await api.call(`users/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        hideLoading();
        const updatedWorkForce = workForce?.data?.map(worker =>
          Number(worker.id) === Number(id) ? {
            ...response.data.user,
          } : worker
        );
        setWorkForce(updatedWorkForce);
        return { status: response.status };
      } else {
        hideLoading();
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update workforce member'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      hideLoading();
      handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update workforce member'
      });
      return {
        status: 'error',
        error: error
      };
    }
  };

  const deleteWorkForce = async (id) => {
    if (!api) {
      return;
    }

    try {
      const response = await api.call(`users/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/company-workforces'));
        return { status: response.status };
      } else {
        hideLoading();
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete workforce member'
        });
      }
    } catch (error) {
      hideLoading();
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete workforce member'
      });
    }
  };

  return (
    <CompanyWorkForceContext.Provider
      value={{
        workForce,
        addWorkForce,
        getWorkForce: (page, filters) => getWorkForce(page, filters),
        getWorkForceById,
        updateWorkForce,
        deleteWorkForce,
        formErrors,
        paginate,
        clearErrors,
        getAccessPermissions: (type) => getAccessPermissions(type),
        getEmploymentSettings,
        getManagerSupervisor,
        getPayrollSettings,
        setWorkForce,
        getDocumentSettings
      }}
    >
      {children}
    </CompanyWorkForceContext.Provider>
  );
}; 